/**
 * login's analytics event catalog.
 *
 * The tracking package (`@martincastroalvarez/tracking`) is deliberately agnostic about which
 * events exist — each app owns its own names and passes them as a `TrackEvent`'s `name`. This file
 * is that list for login, and it is the whole of it.
 *
 * **The wire strings are frozen.** They are exactly what the retired `useGoogleAnalytics.ts` sent,
 * character for character, because GA4 keys its reports on the event name: renaming one here would
 * not migrate the history, it would start a second, empty report beside the old one and quietly
 * flatline the first. Adopting a shared tracker is not a reason to lose a year of sign-in data.
 *
 * One of them is a misnomer worth knowing about rather than fixing silently. `login_page_view` is
 * fired twice for different things — once when the page mounts, which is what it says, and once at
 * the start of each provider exchange in `useLogin`, which is an *attempt*, not a view. That means
 * the counter reads high and cannot be split after the fact. Correcting it means a new name and a
 * discontinuity in the report, so it is a decision for whoever reads the dashboard, not a
 * side effect of this migration.
 */

/** Event names. Passed as `TrackEvent.name`. */
export const LoginEvents = {
  /** The sign-in page was shown. Also fired at the start of a provider exchange — see above. */
  PAGE_VIEW: 'login_page_view',
  /** A session JWT came back and the cookie was written. */
  SUCCESS: 'login_success',
  /** Any provider or session-exchange failure. Not Google-specific despite the name. */
  OAUTH_ERROR: 'google_oauth_error',
} as const;

/** The single reporting category every login event carries. */
export const AUTHENTICATION = 'Authentication';
